import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useEnquiry } from "@/context/EnquiryContext";
import { img } from "@/api";
import { Menu, X, ChevronDown, Cog, ArrowRight, Phone } from "lucide-react";

export default function Header() {
  const site = useSite();
  const { open } = useEnquiry();
  const nav = useNavigate();
  const [mobile, setMobile] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const [mobileSub, setMobileSub] = useState(null);
  const s = (site && site.site) || {};
  const baseMenu = (site && site.navigation && site.navigation.header) || [];
  const categories = (site && site.categories) || [];
  const brand = s.brand_name || "EKTA WIRE MACHINES";
  const suffix = s.brand_suffix || "";
  const logo = img(s.logo_url || "");
  const logoHeight = Number(s.logo_height) || 52;
  const logoHeightMobile = Number(s.logo_height_mobile) || 38;
  const menuFont = Number(s.menu_font_size) || 14;
  const headerPhone = s.header_phone || {};
  const phoneNumber = headerPhone.number || "";
  const phoneTel = phoneNumber.replace(/[^\d+]/g, "");

  // Auto-populate the "Machines" dropdown from live categories.
  const menu = baseMenu.map((n) => {
    if (/machines/i.test(n.label) && categories.length) {
      return { ...n, children: categories.map((c) => ({ label: c.name, href: `/machines/${c.slug}` })) };
    }
    return n;
  });

  const goTo = (href) => { setMobile(false); setMobileSub(null); if (href.startsWith("http")) window.location.href = href; else nav(href); };

  return (
    <header className="sticky top-0 z-50 bg-white border-b" style={{ borderColor: "var(--border-c)" }} data-testid="site-header">
      <div className="ekta-container flex items-center justify-between gap-4" style={{ minHeight: 74 }}>
        <Link to="/" className="flex items-center gap-3 py-2" onClick={() => setMobile(false)}>
          {logo ? <img src={logo} alt={brand} className="ekta-logo" style={{ "--logo-h": `${logoHeight}px`, "--logo-h-mobile": `${logoHeightMobile}px`, width: "auto", maxHeight: 90 }} /> : (
            <>
              <div className="flex h-12 w-12 items-center justify-center rounded-md text-white" style={{ background: "var(--brand-primary)" }}><Cog size={24} /></div>
              <div className="leading-tight">
                <div className="font-bold tracking-tight" style={{ color: "var(--ink)", fontFamily: "var(--font-heading)", fontSize: 18 }}>{brand}</div>
                {suffix && <div className="ekta-eyebrow" style={{ color: "var(--muted-ink)", fontSize: "0.66rem" }}>{suffix} · Rajkot, India</div>}
              </div>
            </>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5">
          {menu.map((n) => (
            <div key={n.label} className="relative" onMouseEnter={() => setOpenDrop(n.label)} onMouseLeave={() => setOpenDrop(null)}>
              <Link to={n.href} className="flex items-center gap-1 rounded-md px-2.5 py-2 font-bold uppercase tracking-wide whitespace-nowrap hover:text-[color:var(--brand-primary)] transition-colors" style={{ color: "var(--ink-2)", fontSize: menuFont }}>
                {n.label}{n.children && n.children.length > 0 && <ChevronDown size={13} style={{ color: "var(--muted-ink)" }} />}
              </Link>
              {n.children && n.children.length > 0 && openDrop === n.label && (
                <div className="absolute left-0 top-full pt-1 z-50">
                  <div className="min-w-[260px] rounded-md border bg-white py-2 shadow-lg" style={{ borderColor: "var(--border-c)" }}>
                    {n.children.map((c) => (
                      <button key={c.label} onClick={() => goTo(c.href)} className="block w-full text-left px-4 py-2 text-sm hover:bg-[var(--bg-2)] transition-colors" style={{ color: "var(--ink-2)" }}>{c.label}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {headerPhone.enabled !== false && phoneNumber && (
            <a href={`tel:${phoneTel}`} className="hidden md:inline-flex items-center gap-2 rounded-md px-3 py-2 font-bold whitespace-nowrap" style={{ color: "var(--brand-primary)", fontSize: menuFont }} data-testid="header-phone">
              <Phone size={16} /> {phoneNumber}
            </a>
          )}
          <button onClick={() => open({ contactMode: true })} className="ekta-btn ekta-btn-primary hidden sm:inline-flex" data-testid="header-get-in-touch">Get in Touch <ArrowRight size={16} /></button>
          <button aria-label="Menu" className="lg:hidden flex h-11 w-11 items-center justify-center rounded-md border" style={{ borderColor: "var(--border-c)" }} onClick={() => setMobile(!mobile)} data-testid="header-mobile-toggle">{mobile ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden border-t bg-white max-h-[80vh] overflow-y-auto" style={{ borderColor: "var(--border-c)" }}>
          <div className="ekta-container py-3 flex flex-col">
            {menu.map((n) => {
              const hasKids = n.children && n.children.length > 0;
              const expanded = mobileSub === n.label;
              return (
                <div key={n.label} className="border-b" style={{ borderColor: "var(--bg-3)" }}>
                  <div className="flex items-center justify-between">
                    <button onClick={() => (hasKids ? setMobileSub(expanded ? null : n.label) : goTo(n.href))} className="flex-1 text-left py-3 text-sm font-bold uppercase tracking-wide" style={{ color: "var(--ink-2)" }} data-testid={`mobile-nav-${n.label}`}>{n.label}</button>
                    {hasKids && <button aria-label="Toggle submenu" onClick={() => setMobileSub(expanded ? null : n.label)} className="p-3"><ChevronDown size={18} style={{ color: "var(--muted-ink)", transform: expanded ? "rotate(180deg)" : "none", transition: "transform 150ms" }} /></button>}
                  </div>
                  {hasKids && expanded && (
                    <div className="pb-2">
                      <button onClick={() => goTo(n.href)} className="block w-full text-left py-2 pl-4 text-sm font-semibold" style={{ color: "var(--brand-primary)" }}>View {n.label} page →</button>
                      {n.children.map((c) => (
                        <button key={c.label} onClick={() => goTo(c.href)} className="block w-full text-left py-2 pl-4 text-sm" style={{ color: "var(--muted-ink)" }}>{c.label}</button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {phoneNumber && <a href={`tel:${phoneTel}`} className="flex items-center gap-2 py-3 font-bold" style={{ color: "var(--brand-primary)" }}><Phone size={16} /> {phoneNumber}</a>}
            <button onClick={() => { setMobile(false); open({ contactMode: true }); }} className="ekta-btn ekta-btn-primary mt-3">Get in Touch <ArrowRight size={16} /></button>
          </div>
        </div>
      )}
    </header>
  );
}
